import { supabase } from './supabase';

export interface ColorPreset {
  id: string;
  business_id: string;
  title: string;
  primary_color: string;
  secondary_color?: string;
  created_at: string;
  updated_at: string;
}

export type CreateColorPresetData = Omit<ColorPreset, 'id' | 'created_at' | 'updated_at'>;

/**
 * Get all color presets for a business
 */
export async function getColorPresets(businessId: string): Promise<ColorPreset[]> {
  if (!supabase) {
    throw new Error('Supabase client is not initialized');
  }

  const { data, error } = await supabase
    .from('color_presets')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data as ColorPreset[];
}

/**
 * Create a new color preset
 */
export async function createColorPreset(preset: CreateColorPresetData): Promise<ColorPreset> {
  if (!supabase) {
    throw new Error('Supabase client is not initialized');
  }

  const { data, error } = await supabase
    .from('color_presets')
    .insert([preset])
    .select()
    .single();

  if (error) throw error;
  return data as ColorPreset;
}

/**
 * Update an existing color preset
 */
export async function updateColorPreset(
  id: string, 
  updates: Partial<Pick<ColorPreset, 'title' | 'primary_color' | 'secondary_color'>>
): Promise<ColorPreset> {
  if (!supabase) {
    throw new Error('Supabase client is not initialized');
  }

  const { data, error } = await supabase
    .from('color_presets')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as ColorPreset;
}

/**
 * Delete a color preset
 */
export async function deleteColorPreset(id: string): Promise<void> {
  if (!supabase) {
    throw new Error('Supabase client is not initialized');
  }

  const { error } = await supabase
    .from('color_presets')
    .delete()
    .eq('id', id);

  if (error) throw error;
} 